import { Link } from 'react-router-dom';
import { Search, ArrowDown, ArrowUp } from 'lucide-react';

export interface TimelineAlert {
  id: number;
  message: string;
  raw_event: string | null;
  source: string | null;
  created_at: string;
  rule_name: string | null;
  rule_keyword: string | null;
  rule_mitre_tactic: string | null;
  rule_mitre_technique: string | null;
  parsed: {
    format: string;
    fields?: Record<string, string>;
    normalized?: {
      srcIp?: string; srcPort?: string; dstIp?: string; dstPort?: string;
      protocol?: string; action?: string; user?: string; threat?: string;
      vendor?: string; product?: string;
    };
    cef?: {
      deviceVendor: string;
      deviceProduct: string;
      name: string;
      extensions: Record<string, string>;
    };
  } | null;
}

function formatGap(from: string, to: string): string {
  const seconds = Math.abs(Math.round((new Date(to).getTime() - new Date(from).getTime()) / 1000));
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] text-gray-400 mb-0.5">{label}</div>
      <div className="text-sm text-gray-800">{children}</div>
    </div>
  );
}

// Timeline verticale dell'incident: gli alert dal più recente al più vecchio, con l'intervallo
// di tempo trascorso fra uno e l'altro e i campi investigativi estratti dal raw event.
export default function IncidentTimeline({ alerts }: { alerts: TimelineAlert[] }) {
  const ordered = [...alerts].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  if (ordered.length === 0) return <p className="text-sm text-gray-400 p-4">No alert linked to this incident yet.</p>;

  return (
    <div className="p-4">
      <div className="flex items-center gap-3 mb-3 text-xs text-gray-400">
        <span className="w-24 text-right">Now</span>
        <span className="w-2 h-2 rounded-full border border-gray-300" />
        <span className="badge bg-gray-100 text-gray-500 flex items-center gap-1"><ArrowDown size={11} /> Forward</span>
      </div>

      {ordered.map((a, i) => {
        const norm = a.parsed?.normalized || {};
        const deviceSource = a.parsed?.cef
          ? `${a.parsed.cef.deviceVendor} ${a.parsed.cef.deviceProduct}`
          : [norm.vendor, norm.product].filter(Boolean).join(' ') || a.source || 'syslog';
        return (
          <div key={a.id}>
            <div className="flex gap-3">
              <div className="w-24 shrink-0 text-right">
                <div className="text-xs text-gray-500">{new Date(a.created_at).toLocaleDateString('en-GB')}</div>
                <div className="text-xs text-gray-800 font-medium">{new Date(a.created_at).toLocaleTimeString('en-GB')}</div>
                {i === ordered.length - 1 && <div className="text-[11px] text-primary-600 mt-0.5">First alert</div>}
              </div>
              <div className="relative flex flex-col items-center shrink-0">
                <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">
                  {ordered.length - i}
                </span>
                <span className="flex-1 w-px bg-gray-200 mt-1" />
              </div>

              <div className="flex-1 border border-gray-200 rounded-lg mb-2">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50/60">
                  <Link to={`/alerts/${a.id}`} className="text-sm font-medium text-primary-700 hover:underline">
                    {a.rule_name || 'Unclassified alert'}
                  </Link>
                  <span className="ml-auto text-xs text-gray-400">
                    Device Source <span className="text-gray-700">{deviceSource}</span>
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-3 py-3">
                  <Field label="Category">{a.rule_name || '—'}</Field>
                  <Field label="Phase">{a.rule_mitre_tactic || '—'}</Field>
                  <Field label="Src IP">{norm.srcIp ? `${norm.srcIp}${norm.srcPort ? `:${norm.srcPort}` : ''}` : '—'}</Field>
                  <Field label="Dst IP">{norm.dstIp ? `${norm.dstIp}${norm.dstPort ? `:${norm.dstPort}` : ''}` : '—'}</Field>
                  <Field label="Intelligence Tag">
                    {a.rule_keyword ? <span className="badge bg-red-50 text-red-600">{a.rule_keyword}</span> : '—'}
                  </Field>
                  <Field label="Attack Result">
                    {norm.action ? <span className="text-red-600">{norm.action}</span> : 'observed'}
                  </Field>
                  <Field label="Technique">{a.rule_mitre_technique || '—'}</Field>
                  <Field label="Log Format">{a.parsed?.format?.toUpperCase() || '—'}</Field>
                  <div className="col-span-2 md:col-span-4">
                    <details>
                      <summary className="text-xs text-primary-600 cursor-pointer flex items-center gap-1 w-fit">
                        <Search size={11} /> Raw event
                      </summary>
                      <pre className="mt-1 bg-gray-50 border border-gray-200 rounded-lg p-2 text-xs text-gray-600 whitespace-pre-wrap break-all">
                        {a.raw_event || a.message}
                      </pre>
                    </details>
                  </div>
                </div>
              </div>
            </div>

            {i < ordered.length - 1 && (
              <div className="flex gap-3 items-center mb-2">
                <span className="w-24 text-right text-[11px] text-gray-400">
                  (Interval: {formatGap(ordered[i + 1].created_at, a.created_at)})
                </span>
                <span className="w-4 flex justify-center"><span className="w-px h-6 bg-gray-200" /></span>
              </div>
            )}
          </div>
        );
      })}

      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
        <span className="w-24 text-right">
          <span className="badge bg-gray-100 text-gray-500 flex items-center gap-1 w-fit ml-auto">
            <ArrowUp size={11} /> Backward
          </span>
        </span>
        <span className="w-2 h-2 rounded-full border border-gray-300" />
      </div>
    </div>
  );
}
